import { useAsyncEffect } from '@digital-spiders/react-hooks';
import { getHookPointStoredTrackingData } from '@hook-point/tracking-data';
import * as Sentry from '@sentry/astro';
import React, { useState } from 'react';
import type { Closer } from '../api/types';
import { ROOT_DOMAIN, VIDEO_TYPES_MAP } from '../constants';
import type { RawPortableText } from '../types/types';
import { flatten } from '@digital-spiders/nodash';
import { slugify } from '@digital-spiders/misc-utils';

export function getModuleId(id?: string, title?: string) {
  return id || (title && slugify(title));
}

export type StrPartPreprocesser = (str: string) => React.ReactNode;

export function wrapStrPartsBySplitter(
  str: string,
  splitter: string | RegExp,
  wrapStrPart: (strPart: React.ReactNode, index: number) => React.ReactNode,
  preprocessStrPart: StrPartPreprocesser = str => str,
): React.ReactNode {
  const strParts = str.split(splitter).map(strPart => preprocessStrPart(strPart));
  if (strParts.length % 2 === 0) {
    throw new Error(
      `String incorrectly formatted, got even number of parts after split. splitRegex: ${splitter}   str: ${str}`,
    );
  }
  return strParts.map((string, i) => (i % 2 !== 0 ? wrapStrPart(string, i) : string));
}

export function replaceSplitterWithEl(
  str: string,
  splitter: string | RegExp,
  el: React.ReactNode,
  preprocessStrPart: StrPartPreprocesser = str => str,
): React.ReactNode {
  const splitStr = str.split(splitter).map(strPart => preprocessStrPart(strPart));
  const splitStrWithEls: Array<React.ReactNode> = [];
  for (let i = 0; i < splitStr.length; i++) {
    splitStrWithEls.push(<React.Fragment key={i * 2}>{splitStr[i]}</React.Fragment>);
    splitStrWithEls.push(<React.Fragment key={i * 2 + 1}>{el}</React.Fragment>);
  }
  splitStrWithEls.pop();
  return splitStrWithEls;
}

export function replaceNewLinesWithBr(
  str: string,
  preprocessStrPart: StrPartPreprocesser = str => str,
): React.ReactNode {
  return replaceSplitterWithEl(str, '\n', <br></br>, preprocessStrPart);
}

export function getVideoTypeByUrl(url: string): keyof typeof VIDEO_TYPES_MAP {
  const type = Object.values(VIDEO_TYPES_MAP).find(({ formatRegex }) =>
    url.trim().match(formatRegex),
  )?.type;
  if (!type) {
    throw new Error(
      `Got video url "${url}" that didn't match any known type: ${Object.keys(VIDEO_TYPES_MAP).join(
        ', ',
      )}`,
    );
  }
  return type;
}

export function useCloser(closers: Array<Closer>) {
  const [closer, setCloser] = useState<Closer | null | undefined>(undefined);

  useAsyncEffect(async () => {
    let trackingData = null;
    try {
      trackingData = await getHookPointStoredTrackingData({
        rootDomain: ROOT_DOMAIN,
      });
    } catch (error) {
      console.error('Error getting tracking data', error);
      Sentry.captureException(error);
      return;
    }

    setCloser(
      (trackingData.closer &&
        closers.find(closer => closer.slug.current === trackingData.closer)) ||
        null,
    );
  }, []);

  return closer;
}
